import { HierarcicalObjectReference } from "@novorender/data-js-api";
import pMap from "p-map";
import { useEffect, useRef } from "react";

import { useExplorerGlobals } from "contexts/explorerGlobals";
import { type ObjectDataAccess } from "dataAccess";
import { useDbStatus } from "hooks/useDbStatus";
import { isLeafNode } from "utils/objectPath";
import { startMeasure } from "utils/performanceMeasure";

/**
 * Maximum concurrency for preloading descendant IDs.
 * Kept low to avoid competing with user-initiated requests.
 */
const PRELOAD_CONCURRENCY = 3;

/**
 * Eagerly preloads `descendants` arrays for the top-level nodes in the scene
 * as soon as the database is available. This warms the in-memory cache on
 * `HierarcicalObjectReference.descendants` so that subsequent "Select All"
 * operations resolve instantly without network calls.
 *
 * The hook runs once per scene load and operates in the background with low
 * concurrency to avoid competing with user-initiated requests.
 *
 * Place this hook high in the component tree (e.g., in the Explorer or App
 * component) so it runs regardless of whether the Object Tree panel is open.
 */
export function useEagerDescendantsPreload({ skip = false }: { skip?: boolean } = {}): void {
    const { state } = useExplorerGlobals();
    const dataAccess = state?.dataAccess;
    const dbStatus = useDbStatus();
    const lastPreloaded = useRef<ObjectDataAccess | null>(null);

    useEffect(() => {
        // Skipped while the hierarchy can't be read (offline without a downloaded index, or
        // the scene DB is being prepared) — the preload could only fire doomed requests.
        if (skip || !dataAccess || !dbStatus.isAvailable || lastPreloaded.current === dataAccess) {
            return;
        }
        lastPreloaded.current = dataAccess;

        const abortController = new AbortController();
        const signal = abortController.signal;
        let finished = false;

        void preloadRootDescendants(dataAccess, signal).finally(() => {
            finished = true;
        });

        return () => {
            abortController.abort();
            // Let an interrupted run (e.g. a `202` mid-preload) start over once the DB is back.
            if (!finished) {
                lastPreloaded.current = null;
            }
        };
    }, [dataAccess, dbStatus.isAvailable, skip]);
}

async function preloadRootDescendants(dataAccess: ObjectDataAccess, signal: AbortSignal): Promise<void> {
    // Standalone measurement: this background warm-up runs at the tail of viewer
    // init and outlives the VIEWER_LOAD span, so it's tracked as its own root
    // trace rather than nested under viewer load.
    const perf = startMeasure("eagerDescendantsPreload");
    try {
        // Fetch the immediate children of the root (depth 1) and collect them.
        // `for await...of` closes the underlying generator (calls .return()) when
        // we break on abort, unlike a manual .next() loop.
        const topLevelNodes: HierarcicalObjectReference[] = [];
        for await (const node of dataAccess.getChildren(undefined, signal)) {
            if (signal.aborted) {
                break;
            }
            topLevelNodes.push(node);
        }

        if (signal.aborted) {
            perf.end({ aborted: true });
            return;
        }

        perf.mark("topLevelLoaded", { topLevelCount: topLevelNodes.length });

        // Filter to non-leaf nodes that don't already have descendants cached
        const foldersToPreload = topLevelNodes.filter((node) => !isLeafNode(node) && node.descendants === undefined);

        if (foldersToPreload.length === 0) {
            perf.end({ topLevelCount: topLevelNodes.length, foldersToPreload: 0 });
            return;
        }

        // Preload descendants with limited concurrency to avoid competing with
        // user-initiated requests. `signal` makes pMap stop scheduling and reject
        // with AbortError on unmount (swallowed by the outer catch).
        await pMap(
            foldersToPreload,
            // Fast-path only: a scene whose descendants files are absent must not
            // trigger the unbounded subtree-search fallback here, or every
            // top-level folder would spawn thousands of paginated requests that
            // starve user-initiated selections. Failures are best-effort — the
            // user pays the normal fetch delay on first use for that node.
            (node) => dataAccess.warmDescendantIds(node, signal),
            { concurrency: PRELOAD_CONCURRENCY, signal },
        );

        perf.end({ topLevelCount: topLevelNodes.length, foldersPreloaded: foldersToPreload.length });
    } catch {
        // Silently ignore — preloading is best-effort. pMap rejects with an
        // AbortError when the signal fires (unmount / backend change); treat that as
        // an aborted run rather than a failure.
        perf.end({ aborted: signal.aborted, error: !signal.aborted });
    }
}
